import type { MessageDescriptor } from 'react-intl';

import { token } from '@atlaskit/tokens';

import DailyBriefIcon from './images/DailyBriefIcon.svg';
import DiscoveryFeedbackIcon from './images/DiscoveryFeedbackIcon.svg';
import DiscoveryFeedbackPreviewIcon from './images/DiscoveryFeedbackPreviewIcon.svg';
import ProjectPlannerIcon from './images/ProjectPlannerIcon.svg';
import ProjectPlannerPreviewIcon from './images/ProjectPlannerPreviewIcon.svg';
import DailyBriefPreviewIcon from './images/DailyBriefPreviewIcon.svg';
import { i18n } from './messages.ts';

/**
 * Static configuration for the "Rovo Agents for you" cards.
 *
 * PR2 (CCCEMAU-3070): card visuals + responsive layout only. The
 * `agentExternalConfigReference` field is a placeholder for PR4 (Try Now → Rovo
 * sidebar). We reference the OOTB agent by its external config reference rather
 * than an agent id, since the underlying agent id can change. The final values
 * are owned by the Rovo Agent team and are not wired yet.
 *
 * Agent `icon` (top-left badge) and `previewIcon` (preview caption glyph)
 * assets are product SVGs exported from Figma (colours baked in) and rendered
 * as `<img>` sources. `accentColor` is the pastel colour of the stacked-paper
 * "backing sheet" that peeks out behind the white preview sheet.
 */
export type AgentCardConfig = {
	/** Stable key used for React keys, test ids and (later) dismiss persistence. */
	key: string;
	/**
	 * External config reference for the OOTB agent (the stable identifier used to
	 * launch it). Placeholder until wired up in PR4. Preferred over a raw agent id
	 * because the agent id can change while the OOTB reference is stable.
	 */
	agentExternalConfigReference: string;
	/** URL to the agent's top-left badge SVG icon asset (colours baked in). */
	icon: string;
	/** URL to the preview-caption glyph SVG (sparkle / target / lightbulb). */
	previewIcon: string;
	/** Pastel accent colour token for the preview backing sheet. */
	accentColor: string;
	/**
	 * Exact rotation (degrees) of the white preview sheet, matching the Figma
	 * illustration. Each card tilts a slightly different amount/direction, so the
	 * stacked-paper look is unique per card (e.g. Daily Brief +2°, Project planner
	 * -1.8°, Discovery & Feedback +1.8°).
	 */
	previewTilt: number;
	title: MessageDescriptor;
	description: MessageDescriptor;
	previewCaption: MessageDescriptor;
};

export const AGENT_CARDS: AgentCardConfig[] = [
	{
		key: 'daily-brief',
		agentExternalConfigReference: '',
		icon: DailyBriefIcon,
		previewIcon: DailyBriefPreviewIcon,
		accentColor: token('color.background.warning'),
		previewTilt: -2,
		title: i18n.dailyBriefTitle,
		description: i18n.dailyBriefDescription,
		previewCaption: i18n.dailyBriefPreviewCaption,
	},
	{
		key: 'project-planner',
		agentExternalConfigReference: '',
		icon: ProjectPlannerIcon,
		previewIcon: ProjectPlannerPreviewIcon,
		accentColor: token('color.background.discovery'),
		previewTilt: -1.8,
		title: i18n.projectPlannerTitle,
		description: i18n.projectPlannerDescription,
		previewCaption: i18n.projectPlannerPreviewCaption,
	},
	{
		key: 'discovery-feedback',
		agentExternalConfigReference: '',
		icon: DiscoveryFeedbackIcon,
		previewIcon: DiscoveryFeedbackPreviewIcon,
		accentColor: token('color.background.success'),
		previewTilt: 1.8,
		title: i18n.discoveryFeedbackTitle,
		description: i18n.discoveryFeedbackDescription,
		previewCaption: i18n.discoveryFeedbackPreviewCaption,
	},
];
